import { Box, SxProps, Typography } from '@mui/material';
import { AlertCard, FailedMessageWithHref } from 'common/ui';
import { useGetCalendarBookingQuery } from 'generated-graphql/hooks/apollo-hooks';
import {
  calendarBookingFormatScheduledFor,
  calendarBookingStatusText,
  calendarBookingStatusType
} from '../utils/calendarBookingUtils';
import { TextBlock } from 'common/ui/TextBlock';
import { Loading } from 'components/Loading';
import { Button } from 'system/atoms';
import { useConfirmationModal } from 'services/modals/hooks';
import { ConfirmationModalType } from 'services/modals/constants';
import { useCancelBooking } from '../api/useCancelBooking';
import { useLoadingOverlay } from 'services/loading/hooks';
import { ToastType, useToast } from 'services/toast';
import { useRequestBookingReschedule } from '../api/useRequestRescheduleBooking';
import { MeetingBookingStatus } from 'generated-graphql/graphql';

const container: SxProps = {
  width: '479px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px'
};

const loadingContainer: SxProps = {
  width: '479px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center'
};

type Props = {
  calendarBookingId: string;
};

export { type Props as CalendarBookingDetailsDrawerProps };

export const CalendarBookingDetailsDrawer: React.FC<Props> = ({
  calendarBookingId
}) => {
  const { data, loading, error, updateQuery } = useGetCalendarBookingQuery({
    variables: {
      id: calendarBookingId as string
    }
  });
  const { mutateAsync: cancelBooking } = useCancelBooking();
  const { mutateAsync: requestBookingReschedule } =
    useRequestBookingReschedule();

  const { showModal } = useConfirmationModal();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const { showToast } = useToast();

  if (loading) {
    return (
      <Box sx={loadingContainer}>
        <Loading />
      </Box>
    );
  }

  if (!data?.getCalendarBooking || error) {
    return (
      <Box sx={loadingContainer}>
        <Typography variant="h5">Error loading meeting details</Typography>
      </Box>
    );
  }

  const {
    status,
    calendarMeetingTemplate,
    organizerUserCalendar,
    candidateName,
    startDateTime,
    endDateTime,
    conversationId
  } = data.getCalendarBooking;

  const updateCalendarBookingStatus = (status: MeetingBookingStatus) => {
    updateQuery((prevData) => {
      if (!prevData.getCalendarBooking) return prevData;
      return {
        ...prevData,
        getCalendarBooking: {
          ...prevData.getCalendarBooking,
          status: status
        }
      };
    });
  };

  const onCancelClick = () => {
    showModal({
      title: 'Cancel meeting?',
      message:
        'A cancellation email will be sent to the candidate and meeting panel. ',
      modalType: ConfirmationModalType.DESTRUCTIVE,
      destructiveText: 'Cancel meeting',
      cancelText: 'Do not cancel',
      onDestructiveConfirm: async () => {
        try {
          showLoadingOverlay();

          await cancelBooking({ conversationId });

          updateCalendarBookingStatus(MeetingBookingStatus.Cancelled);

          showToast({
            title: 'Meeting cancelled',
            message: 'The candidate and meeting panel have been notified.',
            type: ToastType.Success
          });
        } catch {
          showToast({
            title: 'Something went wrong',
            message: (
              <FailedMessageWithHref
                failedMessage="Try cancelling this meeting again."
                emailSubject="Failed to cancel meeting"
              />
            ),
            type: ToastType.Error
          });
        } finally {
          hideLoadingOverlay();
        }
      }
    });
  };

  const onRescheduleClick = () => {
    showModal({
      title: 'Reschedule meeting?',
      message: `The candidate will be asked to choose another time slot.`,
      confirmationText: 'Do not reschedule',
      cancelText: 'Reschedule',
      onClose: async () => {
        try {
          showLoadingOverlay();

          await requestBookingReschedule({ conversationId });

          updateCalendarBookingStatus(
            MeetingBookingStatus.ReschedulingRequested
          );

          showToast({
            title: 'Candidate asked to reschedule',
            message:
              'The candidate has been asked to choose another time slot.',
            type: ToastType.Success
          });
        } catch {
          showToast({
            title: 'Something went wrong',
            message: (
              <FailedMessageWithHref
                failedMessage="Reschedule this meeting again."
                emailSubject="Failed to reschedule meeting"
              />
            ),
            type: ToastType.Error
          });
        } finally {
          hideLoadingOverlay();
        }
      }
    });
  };

  return (
    <Box sx={container}>
      <Typography variant="h5" fontSize={30} fontWeight="400">
        Meeting details
      </Typography>
      <AlertCard
        type={calendarBookingStatusType[status]}
        title={calendarBookingStatusText[status]}
      />
      <TextBlock
        variant="field"
        title="Scheduled for"
        subtitle={calendarBookingFormatScheduledFor(startDateTime, endDateTime)}
      />
      <TextBlock
        variant="field"
        title="Meeting type"
        subtitle={calendarMeetingTemplate?.title}
      />
      <TextBlock
        variant="field"
        title="Organiser"
        subtitle={organizerUserCalendar.name}
      />
      <TextBlock variant="field" title="Candidate" subtitle={candidateName} />
      {status !== MeetingBookingStatus.Cancelled && (
        <Box display="flex" gap={2}>
          {status !== MeetingBookingStatus.ReschedulingRequested && (
            <Button variant="primary" onClick={onRescheduleClick}>
              Reschedule
            </Button>
          )}
          <Button onClick={onCancelClick} state="destructive">
            Cancel meeting
          </Button>
        </Box>
      )}
    </Box>
  );
};
